import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Loader2, ExternalLink, AlertTriangle, ArrowLeft, RefreshCw, FolderOpen, LayoutDashboard, Activity,
  Globe, Landmark, MessageSquareText, Smile, Database, Gauge, BarChart3, ClipboardCheck, ArrowUpRight,
  CheckCircle2, X,
} from 'lucide-react'
import api from '../../services/api'
import ProductMetrics, { UsageNote } from './ProductMetrics.jsx'
import { useProductMetrics, usageStats } from './productUsage.js'
import ViewChart, { ViewTabs } from './ViewChart.jsx'
import ScreenForm from './ScreenForm.jsx'
import StatCard from './StatCard.jsx'
import Heatmap from './Heatmap.jsx'
import StatusBoard from './charts/StatusBoard.jsx'
import { profileTable, partColumns } from './charts/profile.js'
import { regionFlow, sourceFlow } from './charts/flows.js'
import { leaderInsight, regionInsight, rankInsight } from './charts/insight.js'
import { groupByRegion, countryInfo } from './countries.js'
import { themeFor, themeVars, productLabel, productShortName, screenTitle, statusKind, STATUS_LOOK, cellNumber, numericColumns, canHeatmap, fmtAmount, READY_RAMP } from './dashboardTheme.js'
import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * ProductDashboard — another product's own screens, drawn inside ProTrack.
 *
 * The product describes a screen as plain data; this draws it. That one idea is what lets every
 * future product get dashboards here with no new ProTrack code — it implements two endpoints and
 * its screens appear, whatever roles it happens to use.
 *
 * The product decides which views this person may see and what goes in them; ProTrack passes on
 * who is asking and renders the answer. Nothing that comes back is stored.
 */
/** A small picture for a screen, chosen from its key or title. A product adding a screen we have
 *  never heard of gets a neutral chart icon rather than none, so the tab row stays even. */
function viewIcon(v) {
  const k = `${v?.key || ''} ${v?.title || ''}`.toLowerCase()
  if (k.includes('overview')) return LayoutDashboard
  if (k.includes('readiness')) return ClipboardCheck
  if (k.includes('countr')) return Globe
  if (k.includes('financ')) return Landmark
  if (k.includes('question') || k.includes('ask')) return MessageSquareText
  if (k.includes('satisf')) return Smile
  if (k.includes('fresh') || k.includes('data')) return Database
  if (k.includes('service') || k.includes('quality')) return Gauge
  if (k.includes('usage') || k.includes('time')) return Activity
  return BarChart3
}

// The pictures a table can be shown as, each fetched only when it is first shown.
const WorldMap = lazy(() => import('./WorldMap.jsx'))
const RegionWheel = lazy(() => import('./charts/RegionWheel.jsx'))
const Mosaic = lazy(() => import('./charts/Mosaic.jsx'))
const Flow = lazy(() => import('./charts/Flow.jsx'))
const RibbonRank = lazy(() => import('./charts/RibbonRank.jsx'))

// The figures the usage card above the opening screen already shows, by the label a screen would
// give them. A card that repeats one with the SAME number is the same fact twice, so it is left
// out, hint or no hint. One with a DIFFERENT number (say, the last 30 days) stays.
const USAGE_KEYS = { 'site visits': 'site_visits', 'questions asked': 'questions_asked', 'time spent': 'time_spent' }
const same = (a, b) => String(a ?? '').replace(/,/g, '').trim().toLowerCase() === String(b ?? '').replace(/,/g, '').trim().toLowerCase()

export default function ProductDashboard() {
  const { t } = useTranslation('platform')
  const { productKey } = useParams()
  const navigate = useNavigate()
  const [opening, setOpening] = useState(false)

  // Open the product already signed in, rather than at its login page.
  //
  // The tab is opened FIRST, empty, and pointed at the link afterwards. Browsers only allow
  // window.open during the click itself; opening it after the await is a popup blocked with no
  // explanation, which reads as a dead button.
  //
  // The link is minted here rather than with the page because it lives sixty seconds — long
  // enough to follow, too short to leave a working session sitting in an old tab.
  const openProduct = async (next) => {
    setOpening(true)
    // No 'noopener' here, deliberately. With it, window.open returns NULL by specification — so
    // the handle is lost, the blank tab is orphaned on screen, and the fallback opens the product
    // in a THIRD tab. The protection is kept by nulling opener once we have navigated, which
    // gives the same result without throwing away the reference we need.
    const tab = window.open('', '_blank')
    const send = (url) => {
      if (!tab) { window.location.href = url; return }   // popup blocked: use this tab
      tab.opener = null
      tab.location = url
    }
    try {
      const { data } = await api.post(`/platform/products/${productKey}/handoff`, next ? { next } : undefined)
      send(data.url)
    } catch (e) {
      // Fall back to the product's front page, or to the very page asked for — the product will
      // ask them to sign in. Signing in by hand beats a button that does nothing, and the reason
      // belongs on screen rather than only in a console. `next` is only ever a path that starts
      // with one slash, so this cannot leave the product's own address.
      const base = (product.url || '').replace(/\/+$/, '')
      if (!base) { tab?.close(); return }
      send(next ? `${base}${next}` : base)
      // eslint-disable-next-line no-console
      console.warn('handoff unavailable, opened the product directly:', e?.response?.data?.detail || e)
    } finally { setOpening(false) }
  }

  const [list, setList] = useState(null)      // {available, data:{views:[]}, product, my_role}
  const [active, setActive] = useState(null)  // view key
  const [view, setView] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Bumped by "Try again": a product that did not answer a moment ago often does the next time.
  const [listTry, setListTry] = useState(0)
  const [viewTry, setViewTry] = useState(0)
  // Set when the next load of a screen must not come from the short memory — after the person has
  // sent something into the product, or pressed Try again. Read once, by that load.
  const freshNext = useRef(false)
  const reloadFresh = () => { freshNext.current = true; setViewTry(n => n + 1) }
  // The product's own count of its use. Read here, not by the card, so on the opening screen the
  // figures can sit among that screen's cards instead of on a card of their own above them.
  const metrics = useProductMetrics(productKey)
  const usage = metrics.state?.available ? (metrics.state.data || null) : null
  // What the product said after something was sent into it. Kept here, not inside the form, so it
  // outlives a form the product takes off the screen. Cleared on moving to another screen.
  const [notice, setNotice] = useState(null)
  useEffect(() => { setNotice(null) }, [productKey, active])

  useEffect(() => {
    let alive = true
    setList(null); setActive(null); setView(null); setError(null)
    api.get(`/platform/products/${productKey}/views`)
      .then(r => {
        if (!alive) return
        setList(r.data)
        const first = r.data?.data?.views?.[0]?.key
        if (first) setActive(first)
      })
      .catch(e => { if (alive) setError(e.response?.data?.detail || 'Could not open this product.') })
    return () => { alive = false }
  }, [productKey, listTry])

  useEffect(() => {
    if (!active) return
    let alive = true
    setBusy(true)
    const fresh = freshNext.current
    freshNext.current = false
    api.get(`/platform/products/${productKey}/views/${active}`, fresh ? { params: { fresh: 1 } } : undefined)
      .then(r => { if (alive) setView(r.data) })
      .catch(e => { if (alive) setView({ available: false, reason: e.response?.data?.detail || 'Could not load this screen.' }) })
      .finally(() => { if (alive) setBusy(false) })
    return () => { alive = false }
  }, [productKey, active, viewTry])

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => navigate('/products')} className="text-xs text-gray-500 hover:text-indigo-700 inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> My Products
        </button>
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      </div>
    )
  }
  if (!list) {
    return <div className="p-6 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Opening…</div>
  }

  const product = list.product || {}
  const views = list.data?.views || []
  const theme = themeFor(productKey)
  const label = productLabel(productKey, product.name)
  const goLabel = `Go to ${productShortName(productKey, product.name)}`
  const usageCard = (
    <div className="mt-5 bg-white rounded-2xl border border-gray-200 shadow-xs p-4">
      <ProductMetrics productName={label} metrics={metrics} />
    </div>
  )

  return (
    <div className="p-6 max-w-6xl mx-auto" style={themeVars(theme)}>
      <button onClick={() => navigate('/products')} className="text-xs text-gray-500 hover:text-[var(--pt-accent)] inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> My Products
      </button>

      {/* Who and where: the product, the person's role in it, and the two ways out. The banner is
          in the product's own colours, so arriving here feels like arriving at the product. */}
      <div className="relative overflow-hidden rounded-3xl text-white shadow-lg px-5 py-5 sm:px-7 sm:py-6" style={{ background: theme.hero }}>
        <span className="pointer-events-none absolute -right-10 -top-16 w-56 h-56 rounded-full bg-white/10" aria-hidden="true" />
        <span className="pointer-events-none absolute right-24 -bottom-24 w-56 h-56 rounded-full bg-white/[0.07]" aria-hidden="true" />
        <span className="pointer-events-none absolute left-1/3 -top-8 w-24 h-24 rounded-full bg-white/[0.06]" aria-hidden="true" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <span className="w-14 h-14 shrink-0 rounded-2xl bg-white/20 ring-1 ring-white/30 backdrop-blur flex items-center justify-center text-3xl leading-none">
              {product.icon || '📊'}
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight tracking-tight">{label}</h1>
              <p className="text-sm text-white/85 mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
                {list.my_role && (
                  <span className="inline-flex items-center gap-1.5">
                    {t('your_role_there')}
                    <span className="rounded-full bg-white/20 ring-1 ring-white/30 px-2.5 py-0.5 text-xs font-semibold">{list.my_role}</span>
                  </span>
                )}
                <span className="text-white/70 text-xs">Shown inside ProTrack. This product decides what you can see here.</span>
              </p>
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {/* Where this product's own people prepare files together, one room per country. */}
            <button onClick={() => navigate(`/products/${productKey}/rooms`)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--pt-accent-dark)] bg-white hover:bg-white/90 rounded-lg px-3.5 py-2 shadow-xs">
              <FolderOpen className="w-3.5 h-3.5" /> Document rooms
            </button>
            {product.url && (
              <button onClick={() => openProduct()} disabled={opening} type="button"
                 className="inline-flex items-center gap-1.5 text-xs font-semibold text-white border border-white/40 hover:bg-white/15 rounded-lg px-3.5 py-2 disabled:opacity-50">
                {opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {goLabel} <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* The product's own screens. */}
      {!list.available ? (
        <>
          {usageCard}
          {list.temporary ? (
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-amber-900 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  <b>{label} didn't answer just now.</b><br />
                  <span className="text-xs">This is usually brief. Its screens will load as soon as it responds.</span>
                </span>
              </p>
              <button type="button" onClick={() => setListTry(n => n + 1)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900 border border-amber-300 bg-white hover:bg-amber-100 rounded-lg px-3 py-2">
                <RefreshCw className="w-3.5 h-3.5" /> Try again
              </button>
            </div>
          ) : (
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-900 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  <b>{t('no_screens_yet')}</b><br />
                  <span className="text-xs">{list.reason}</span>
                </span>
              </p>
              <p className="text-[11px] text-amber-800 mt-2.5">
                When it offers them, they appear here automatically — nothing needs building on this
                side. Until then, use “{goLabel}” above.
              </p>
            </div>
          )}
        </>
      ) : views.length === 0 ? (
        <>
          {usageCard}
          <p className="mt-5 text-sm text-gray-500">
            This product has no screens for your role.
          </p>
        </>
      ) : (
        <>
          {/* The screens, straight under the title, and kept in view while the page scrolls. */}
          <nav aria-label="Screens"
               className="sticky top-2 z-10 mt-5 rounded-2xl border border-gray-200 bg-white/95 backdrop-blur shadow-sm p-1.5">
            <div className="flex gap-1 overflow-x-auto lg:flex-wrap lg:overflow-visible">
              {views.map(v => {
                const Icon = viewIcon(v)
                const on = active === v.key
                return (
                  <button key={v.key} onClick={() => setActive(v.key)} title={v.description || ''}
                    aria-current={on ? 'page' : undefined}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-medium transition ${
                      on ? 'bg-[var(--pt-accent)] text-white shadow-sm' : 'text-gray-600 hover:bg-[var(--pt-accent-soft)] hover:text-[var(--pt-accent-dark)]'}`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    {screenTitle(theme, v.title) || v.key}
                  </button>
                )
              })}
            </div>
          </nav>

          <div className="mt-5 space-y-5">
            {/* Usage belongs to the opening screen. Its figures are drawn among that screen's
                cards; only when the screen itself could not be read do they keep a card of their own. */}
            {active === views[0]?.key && view && !view.available && usageCard}
            {busy && !view ? (
              <p className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
            ) : (
              <RenderView payload={view} onRetry={reloadFresh} busy={busy} theme={theme}
                          usage={active === views[0]?.key ? usage : null}
                          usageStats={active === views[0]?.key && usage ? usageStats(usage) : []}
                          usageNote={active === views[0]?.key ? <UsageNote productName={label} metrics={metrics} /> : null}
                          onAction={openProduct} busyAction={opening}
                          productKey={productKey} viewKey={active}
                          onFormSent={(message) => { setNotice(message); reloadFresh() }} />
            )}
          </div>
        </>
      )}

      {notice && (
        <div role="status" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 max-w-[min(92vw,32rem)] flex items-start gap-2.5 rounded-xl border border-green-200 bg-green-50 text-green-900 shadow-lg px-4 py-3 text-sm">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
          <span className="flex-1">{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Close" className="shrink-0 text-green-700 hover:text-green-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}


/** One `tables[]` block, shown first as the picture its columns suit, with the table itself always
 *  one click away ("Table"). The columns are read from the cells (charts/profile.js):
 *    - countries and figures   -> Map · Regions · Mosaic · Flow (a choice of figure when there are
 *                                 several; Regions, Mosaic and Flow only for figures that add up, and
 *                                 Flow from the source columns when the figures are parts of a whole);
 *    - status words            -> the status board: how much is in place, and what each row lacks;
 *    - figures under years     -> Ranking: who led each year, and by how much;
 *    - a grid of figures       -> Heatmap.
 *  In the table, a cell that is wholly a status word ("Ready", "Missing", "Pending") is drawn as a
 *  coloured pill with a dot and the word itself, so the colour is never the only signal; a column of
 *  plain numbers gets a bar behind each, to the scale of its largest. Everything else is printed as
 *  sent. */
function DataTable({ table: tb, empty, theme }) {
  const rows = useMemo(() => (Array.isArray(tb.rows) ? tb.rows : []), [tb])
  const bars = numericColumns(tb.columns, rows)
  const columns = useMemo(() => (Array.isArray(tb.columns) ? tb.columns : []), [tb])
  const prof = useMemo(() => profileTable(columns, rows.map(r => (Array.isArray(r) ? r : [r]))), [columns, rows])
  const [chosen, setChosen] = useState(null)
  const [metricPick, setMetricPick] = useState(null)

  const metrics = prof.metrics.filter(m => !m.year)
  const metric = metrics.find(m => m.c === metricPick) || metrics.find(m => m.additive) || metrics[0]
  const fmtM = (v) => fmtAmount(v, metric || {})
  const countryRows = prof.countryCol >= 0
  const items = useMemo(() => (countryRows && metric ? rows.map((r, i) => ({
    name: String(r?.[prof.countryCol] ?? ''),
    value: metric.values[i],
    region: prof.regionCol >= 0 ? r?.[prof.regionCol] : undefined,
  })) : []), [countryRows, metric, rows, prof])
  const positive = items.filter(i => i.value > 0).length
  const parts = partColumns(metrics)
  const status = prof.statusCols.length > 0 && rows.length >= 2
  const years = prof.yearCols.length >= 2 && rows.length >= 2
  // Share of each row's status cells that are good — what the readiness map shades.
  const readyItems = useMemo(() => (countryRows && status ? rows.map(r => {
    const ks = prof.statusCols.map(c => statusKind(r?.[c])).filter(k => k && k !== 'mute')
    return { name: String(r?.[prof.countryCol] ?? ''), value: ks.length ? Math.round((ks.filter(k => k === 'good').length / ks.length) * 100) : null }
  }) : []), [countryRows, status, rows, prof])

  const views = []
  if (status) views.push('status')
  if (countryRows && status) views.push('readymap')
  if (years) views.push('rank')
  if (countryRows && metric) {
    views.push('map')
    if (metric.additive && positive >= 3) views.push('regions', 'mosaic', 'flow')
  }
  if (canHeatmap(columns, rows)) views.push('heat')
  views.push('table')
  const mode = views.includes(chosen) ? chosen : views[0]
  const usesMetric = ['map', 'regions', 'mosaic'].includes(mode) || (mode === 'flow' && !parts.length)

  // Ranking over the year columns: the eight rows largest in the latest year.
  const rank = useMemo(() => {
    if (!years) return null
    const ycols = prof.metrics.filter(m => m.year)
    const nameCol = prof.nameCol >= 0 ? prof.nameCol : 0
    const all = rows.map((r, i) => ({ name: String(r?.[nameCol] ?? ''), iso2: countryInfo(r?.[nameCol])?.iso2, values: ycols.map(m => m.values[i]) }))
    const last = ycols.length - 1
    const top = [...all].sort((a, b) => (b.values[last] ?? -Infinity) - (a.values[last] ?? -Infinity)).slice(0, 8)
    return { periods: ycols.map(m => m.header), series: top, fmt: (v) => fmtAmount(v, ycols[0]), more: all.length - top.length }
  }, [years, prof, rows])

  const flowData = useMemo(() => {
    if (mode !== 'flow') return null
    if (parts.length) return sourceFlow(parts.map(m => ({ name: m.header, values: m.values })), rows.map(r => String(r?.[prof.countryCol] ?? '')), theme.series)
    return regionFlow(items, `All · ${metric?.header || 'total'}`)
  }, [mode, parts, rows, prof, items, metric, theme])

  let insight = null
  if (mode === 'rank' && rank) insight = rankInsight(rank.periods, rank.series)
  else if (['map', 'regions', 'mosaic', 'flow'].includes(mode) && items.length) {
    insight = [leaderInsight(items, fmtM), metric?.additive ? regionInsight(groupByRegion(items)) : null].filter(Boolean).join(' ') || null
  } else if (mode === 'readymap') {
    const known = readyItems.filter(r => r.value !== null).sort((a, b) => b.value - a.value)
    const full = known.filter(r => r.value === 100).length
    if (known.length >= 2) insight = `${full} of ${known.length} countries have everything in place; ${known[known.length - 1].name} has the least (${known[known.length - 1].value}%).`
  }

  const lazyBox = (node) => <Suspense fallback={<p className="text-xs text-gray-400" style={{ height: 260 }}>Drawing…</p>}>{node}</Suspense>
  const card = (node) => <div className="rounded-2xl border border-gray-200 bg-white shadow-xs p-4">{node}</div>

  let picture = null
  if (mode === 'status') picture = <StatusBoard columns={columns} rows={rows} statusCols={prof.statusCols} nameCol={prof.nameCol >= 0 ? prof.nameCol : 0} />
  else if (mode === 'readymap') picture = card(lazyBox(<WorldMap items={readyItems} theme={theme} unit="%" fmt={(v, u) => `${v}${u}`} metric="Share ready" ramp={READY_RAMP} />))
  else if (mode === 'rank' && rank) picture = card(lazyBox(<RibbonRank periods={rank.periods} series={rank.series} colors={theme.series} fmt={rank.fmt}
                                                                 note={rank.more > 0 ? `The 8 largest in ${rank.periods[rank.periods.length - 1]}; ${rank.more} more in the Table view.` : ''} />))
  else if (mode === 'map') picture = card(lazyBox(<WorldMap items={items} theme={theme} unit="" fmt={fmtM} metric={metric?.header} />))
  else if (mode === 'regions') picture = card(lazyBox(<RegionWheel items={items} fmt={fmtM} metric={metric?.header} />))
  else if (mode === 'mosaic') picture = card(lazyBox(<Mosaic items={items} fmt={fmtM} metric={metric?.header} />))
  else if (mode === 'flow') picture = card(flowData ? lazyBox(<Flow data={flowData} fmt={fmtM} />) : <p className="text-xs text-gray-500">Nothing to show as a flow.</p>)
  else if (mode === 'heat') picture = <Heatmap columns={columns} rows={rows} theme={theme} />

  return (
    <div className="pt-rise">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          {tb.title && (
            <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <span className="w-1 h-4 rounded-full" style={{ background: 'var(--pt-accent)' }} aria-hidden="true" />
              {tb.title}
            </p>
          )}
          {insight && (
            <p className="mt-1 text-xs text-gray-600 flex items-start gap-1.5">
              <Sparkles className="w-3.5 h-3.5 mt-px shrink-0 text-[var(--pt-accent)]" aria-hidden="true" />{insight}
            </p>
          )}
        </div>
        {rows.length > 0 && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {usesMetric && metrics.length > 1 && (
              <select value={metric?.c} onChange={e => setMetricPick(Number(e.target.value))} aria-label="Figure shown"
                      className="text-[11px] font-semibold text-gray-700 border border-gray-200 rounded-md bg-white px-2 py-1">
                {metrics.filter(m => mode === 'map' || m.additive).map(m => <option key={m.c} value={m.c}>{m.header}</option>)}
              </select>
            )}
            <ViewTabs views={views} mode={mode} onPick={setChosen} />
          </div>
        )}
      </div>
      {picture || (
      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-xs">
        <table className="w-full text-sm">
          {Array.isArray(tb.columns) && tb.columns.length > 0 && (
            <thead>
              <tr className="border-b border-gray-200">
                {tb.columns.map((c, i) => (
                  <th key={i} className="text-left align-bottom px-3.5 py-3 bg-[var(--pt-accent-soft)] text-[11px] uppercase tracking-wide text-[var(--pt-accent-dark)] font-bold">{String(c)}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="border-b border-gray-100 last:border-0 odd:bg-white even:bg-gray-50/50 hover:bg-[var(--pt-accent-soft)]/60 transition-colors">
                {(Array.isArray(row) ? row : [row]).map((cell, ci) => {
                  const text = cell === null || cell === undefined ? '—' : String(cell)
                  const st = statusKind(cell)
                  const n = bars[ci] !== undefined ? cellNumber(cell) : null
                  return (
                    <td key={ci} className={`px-3.5 py-2.5 text-gray-700 align-top ${text.length > 28 ? 'whitespace-normal break-words min-w-[14rem]' : 'whitespace-nowrap'}`}>
                      {st ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                              style={{ background: STATUS_LOOK[st].bg, color: STATUS_LOOK[st].fg }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_LOOK[st].dot }} aria-hidden="true" />
                          {text}
                        </span>
                      ) : n !== null && bars[ci] > 0 ? (
                        <span className="flex items-center gap-2.5">
                          <span className="tabular-nums font-semibold text-gray-900 min-w-[3.5rem]">{text}</span>
                          <span className="hidden sm:block h-1.5 w-24 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
                            <span className="block h-full rounded-full pt-grow" style={{ width: n === 0 ? 0 : `${Math.max(2, (Math.abs(n) / bars[ci]) * 100)}%`, background: 'var(--pt-accent)' }} />
                          </span>
                        </span>
                      ) : text}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="px-3 py-3 text-xs text-gray-400">{empty}</p>}
      </div>
      )}
    </div>
  )
}


/** Draws whatever the product sent. Every part is optional, so a product can start with one
 *  number and grow — a partial screen is better than none, and never an error. */
function RenderView({ payload, theme, busy, onRetry, usage = null, usageStats: usageCards = [], usageNote = null, onAction, busyAction = false, productKey, viewKey, onFormSent }) {
  const { t } = useTranslation('platform')
  if (!payload) return null
  if (!payload.available) {
    return (
      <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0" /> {payload.reason || 'This screen is unavailable.'}
        {payload.temporary && onRetry && (
          <button type="button" onClick={onRetry} className="ml-auto inline-flex items-center gap-1 font-semibold text-amber-900 underline">
            <RefreshCw className="w-3 h-3" /> Try again
          </button>
        )}
      </p>
    )
  }
  const d = payload.data || {}
  // The usage figures first, then the screen's own cards less any that repeat one of them.
  const stats = [...usageCards, ...(Array.isArray(d.stats) ? d.stats : []).filter(s => {
    const key = USAGE_KEYS[String(s?.label || '').trim().toLowerCase()]
    return !(key && usage && same(s?.value, usage[key]))
  })]
  // Buttons that open a page inside the product. The path comes from the product's own screen,
  // so only a plain path with one leading slash is accepted; anything else is not drawn at all.
  const actions = (Array.isArray(d.actions) ? d.actions : []).filter(a =>
    typeof a?.label === 'string' && a.label.trim() && typeof a?.path === 'string'
    && /^\/(?!\/)/.test(a.path) && !a.path.includes('\\'))
  const charts = Array.isArray(d.charts) ? d.charts : []
  // Forms the product put on this screen. One with no key or no fields cannot be filled in or
  // sent, so it is not drawn.
  // What this product's theme keeps off the page (see `hidden` in dashboardTheme.js).
  const hide = theme?.hidden || {}
  const hiddenTables = new Set((hide.tables || []).map(x => String(x).trim().toLowerCase()))
  const forms = hide.forms ? [] : (Array.isArray(d.forms) ? d.forms : []).filter(f =>
    f && typeof f.key === 'string' && f.key && Array.isArray(f.fields) && f.fields.length > 0)
  const tables = (Array.isArray(d.tables) ? d.tables : [])
    .filter(tb => !hiddenTables.has(String(tb?.title || '').trim().toLowerCase()))
  const notes = (Array.isArray(d.notes) ? d.notes : []).filter(n => !(hide.notes && hide.notes.test(String(n))))

  return (
    <div className="space-y-5">
      {(d.title || d.subtitle || usageNote) && (
        <div>
          {d.title && <h2 className="text-xl font-bold text-gray-900">{screenTitle(theme, d.title)}</h2>}
          <span className="block mt-1.5 h-1 w-12 rounded-full" style={{ background: theme.hero }} aria-hidden="true" />
          {d.subtitle && <p className="text-sm text-gray-500 mt-2">{d.subtitle}</p>}
          {usageNote && <div className="mt-3">{usageNote}</div>}
        </div>
      )}

      {actions.length > 0 && onAction && (
        <div className="flex flex-wrap gap-2">
          {actions.map((a, i) => (
            <button key={i} type="button" disabled={busyAction} onClick={() => onAction(a.path)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--pt-accent)] hover:bg-[var(--pt-accent-dark)] disabled:opacity-50 text-white text-xs font-semibold px-3.5 py-2 shadow-xs">
              {a.label} <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      )}

      {stats.length > 0 && (
        <div className={`grid grid-cols-2 gap-3 ${stats.length === 3 ? 'lg:grid-cols-3' : stats.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-4'}`}>
          {stats.map((s, i) => <StatCard key={i} stat={s} colour={theme.series[i % theme.series.length]} />)}
        </div>
      )}

      {charts.length > 0 && (
        <div className="grid gap-3 lg:grid-cols-2">
          {charts.map((c, ci) => <ViewChart key={ci} chart={c} theme={theme} />)}
        </div>
      )}

      {tables.map((tb, ti) => <DataTable key={ti} table={tb} theme={theme} empty={t('nothing_to_show')} />)}

      {forms.length > 0 && productKey && viewKey && (
        <div className="grid gap-4 lg:grid-cols-2 items-start">
          {forms.map(f => (
            <ScreenForm key={f.key} productKey={productKey} viewKey={viewKey} form={f} onSent={onFormSent} />
          ))}
        </div>
      )}

      {notes.map((n, i) => (
        <p key={i} className="text-xs text-gray-500">{String(n)}</p>
      ))}

      {stats.length === 0 && charts.length === 0 && tables.length === 0 && forms.length === 0 && notes.length === 0 && (
        <p className="text-sm text-gray-400">{t('returned_nothing')}</p>
      )}

      {busy && <p className="text-[11px] text-gray-400 flex items-center gap-1.5"><RefreshCw className="w-3 h-3 animate-spin" /> refreshing…</p>}
    </div>
  )
}
